import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import type { SxProps } from '@mui/material'
import Button from '@mui/material/Button'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem'
import { Variant } from '@mui/material/styles/createTypography'
import Typography from '@mui/material/Typography'
import * as React from 'react'
import {
    createContext,
    FC,
    ReactNode,
    useContext,
    useEffect,
    useId,
    useMemo,
    useState,
} from 'react'

import ThemeNew from '../theme/themeNew'

/* Shared context for main and submenu to communicate */
type DropdownCtx = {
    closeAll: () => void
    closeOnClick: boolean
    activeSubmenuKey: string | null
    setActiveSubmenuKey: (k: string | null) => void
}
const DropdownContext = createContext<DropdownCtx | null>(null)
const useDropdownCtx = () => {
    const ctx = useContext(DropdownContext)
    if (!ctx) {
        throw new Error('Dropdown subcomponents must be used inside <Dropdown />')
    }
    return ctx
}

export const DropdownItem: FC<
    MenuItemProps & { leftIcon?: React.ReactNode; onItemClick?: () => void }
> = ({ sx, leftIcon, onClick, onItemClick, children, ...props }) => {
    const { palette } = ThemeNew
    const { closeAll, closeOnClick, setActiveSubmenuKey } = useDropdownCtx()

    const handleClick = (e: React.MouseEvent<HTMLLIElement>) => {
        // Clicking a plain item should close any open submenu and the whole dropdown (if configured)
        setActiveSubmenuKey(null)
        onClick?.(e)
        onItemClick?.()
        if (closeOnClick) closeAll()
    }

    return (
        <MenuItem
            {...props}
            onClick={handleClick}
            sx={{
                color: `Grey900`,
                fontSize: '14px',
                backgroundColor: props.selected ? `${palette.Grey100} !important` : `transparent`,
                ...(sx || {}),
                overflow: 'hidden',
            }}
        >
            {leftIcon && <ListItemIcon sx={{ minWidth: 32 }}>{leftIcon}</ListItemIcon>}
            <ListItemText
                sx={{
                    '> p': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                }}
                primaryTypographyProps={{ variant: 'body2', sx: { color: 'Grey900' } }}
                primary={children}
            />
        </MenuItem>
    )
}

type DropdownSubmenuItemProps = {
    label: ReactNode
    leftIcon?: ReactNode
    children: ReactNode
    iconSx?: SxProps
    menuSx?: SxProps
    itemSx?: SxProps
    submenuKey?: string
}

export const DropdownSubmenuItem: FC<DropdownSubmenuItemProps> = ({
    label,
    leftIcon,
    children,
    iconSx,
    menuSx,
    itemSx,
    submenuKey,
}) => {
    const localId = useId()
    const key = submenuKey ?? localId
    const { palette } = ThemeNew
    const { closeAll, activeSubmenuKey, setActiveSubmenuKey } = useDropdownCtx()

    const isOpen = activeSubmenuKey === key
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    const handleParentClick = (e: React.MouseEvent<HTMLLIElement>) => {
        const el = e.currentTarget as HTMLElement
        if (isOpen) {
            setAnchorEl(null)
            setActiveSubmenuKey(null)
        } else {
            setAnchorEl(el)
            setActiveSubmenuKey(key)
        }
    }

    useEffect(() => {
        if (!isOpen) {
            setAnchorEl(null)
        }
    }, [isOpen])

    const childCtxValue = useMemo<DropdownCtx>(
        () => ({
            closeAll,
            closeOnClick: true,
            activeSubmenuKey,
            setActiveSubmenuKey,
        }),
        [closeAll, activeSubmenuKey, setActiveSubmenuKey],
    )

    return (
        <>
            <MenuItem
                onClick={handleParentClick}
                aria-haspopup="menu"
                aria-expanded={isOpen ? 'true' : undefined}
                sx={{
                    color: `Grey900`,
                    fontSize: '14px',
                    backgroundColor: isOpen ? `${palette.Grey100} !important` : `transparent`,
                    display: 'flex',
                    alignItems: 'center',
                    ...itemSx,
                }}
            >
                {leftIcon ? (
                    <ListItemIcon sx={{ minWidth: 32, ...iconSx }}>{leftIcon}</ListItemIcon>
                ) : null}
                <ListItemText
                    primaryTypographyProps={{ variant: 'body2', sx: { color: 'Grey900' } }}
                    primary={label}
                />
                <ChevronRightIcon fontSize="small" />
            </MenuItem>
            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={() => setActiveSubmenuKey(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{
                    '.MuiPaper-root': {
                        mt: -1,
                        borderRadius: '8px',
                        minWidth: '240px',
                        maxWidth: '280px',
                    },
                    ...(menuSx || {}),
                }}
            >
                <DropdownContext.Provider value={childCtxValue}>
                    {children}
                </DropdownContext.Provider>
            </Menu>
        </>
    )
}

/**
 * Dropdown component
 * @param buttonLabel - label shown on the button
 * @param children - list of `DropdownItem` elements or other react nodes, which will be rendered as dropdown items
 * @param active - sets button's active state; false by default
 * @param closeOnClick - if set to false, prevents dropdown from closing on any item click; true by default
 * @param compact - compact version of the dropdown button; false by default
 * @param menuAutoWidth - forcing the dropdown's width to match its items' width ; false by default
 * @param sx - custom style that applies to dropdown button
 * @param labelSx - custom style that applies to the button label
 * @constructor
 */
const Dropdown: FC<{
    buttonLabel: ReactNode
    typographyVariant?: Variant
    menuPadding?: string
    active?: boolean
    closeOnClick?: boolean
    menuAutoWidth?: boolean
    compact?: boolean
    sx?: SxProps
    labelSx?: SxProps
    paperSx?: SxProps
    dataTestId?: string
    leftIcon?: JSX.Element
    children: ReactNode
}> = ({
    buttonLabel,
    typographyVariant,
    menuPadding,
    children,
    active,
    closeOnClick = true,
    compact,
    menuAutoWidth,
    sx,
    labelSx,
    paperSx,
    leftIcon,
    dataTestId,
    ...rest
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const { spacing } = ThemeNew
    const [activeSubmenuKey, setActiveSubmenuKey] = useState<string | null>(null)

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleCloseAll = () => {
        setActiveSubmenuKey(null)
        setAnchorEl(null)
    }

    const ctxValue = useMemo<DropdownCtx>(
        () => ({
            closeAll: handleCloseAll,
            closeOnClick,
            activeSubmenuKey,
            setActiveSubmenuKey,
        }),
        [closeOnClick, activeSubmenuKey],
    )

    return (
        <DropdownContext.Provider value={ctxValue}>
            <Button
                id={`dropdown-btn-${buttonLabel}`}
                aria-controls={open ? `dropdown-panel-${buttonLabel}` : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                className={active ? 'active' : ''}
                startIcon={leftIcon}
                endIcon={<ArrowDropDownIcon />}
                onClick={handleClick}
                sx={{
                    height: compact ? spacing(4) : spacing(7),
                    color: `Grey900`,
                    borderRadius: '8px',
                    backgroundColor: `Grey100`,
                    fontWeight: 'normal',
                    p: '0 12px',
                    '&:hover': {
                        backgroundColor: `Grey300`,
                    },
                    '&.active': {
                        border: '1px solid',
                        borderColor: `Grey900`,
                    },
                    '.MuiButton-endIcon': {
                        marginLeft: '0 !important',
                    },
                    ...sx,
                }}
                {...rest}
                data-testid={dataTestId}
            >
                <Typography
                    variant={typographyVariant ?? 'body2'}
                    sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        color: 'Grey900',
                        ...labelSx,
                    }}
                >
                    {buttonLabel}
                </Typography>
            </Button>
            <Menu
                id={`dropdown-panel-${buttonLabel}`}
                MenuListProps={{
                    'aria-labelledby': `dropdown-btn-${buttonLabel}`,
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseAll}
                elevation={4}
                data-testid={`${dataTestId}-overlay`}
                sx={
                    {
                        '.MuiPaper-root': {
                            mt: 1,
                            borderRadius: '8px',
                            minWidth: menuAutoWidth ? 'auto' : '280px',
                            ...(menuPadding ? { padding: menuPadding } : {}),
                            ...(paperSx || {}),
                        },
                    } as SxProps<typeof ThemeNew>
                }
            >
                {children}
            </Menu>
        </DropdownContext.Provider>
    )
}

export default Dropdown
